import React from 'react';
import { motion, Variants } from 'framer-motion';

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: (i: number = 0) => ({
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: i * 0.08 },
  }),
};

export const Container: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => <div className={`mx-auto max-w-7xl px-6 lg:px-8 ${className}`}>{children}</div>;

export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.span
    variants={fadeInUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.6 }}
    className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-secondary backdrop-blur-xl"
  >
    {children}
  </motion.span>
);

export const SectionHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  align?: 'center' | 'left';
}> = ({ eyebrow, title, subtitle, align = 'center' }) => (
  <div className={align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <motion.h2
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
      className="text-3xl font-bold leading-[1.15] tracking-tight text-white sm:text-4xl lg:text-5xl"
    >
      {title}
    </motion.h2>
    {subtitle && (
      <motion.p
        variants={fadeInUp}
        custom={1}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        className="mt-6 text-lg leading-relaxed text-brand-muted"
      >
        {subtitle}
      </motion.p>
    )}
  </div>
);

export const GlassCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  index?: number;
}> = ({ children, className = '', index = 0 }) => (
  <motion.div
    variants={fadeInUp}
    custom={index}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.3 }}
    whileHover={{ y: -4 }}
    className={`glass-card group relative rounded-2xl p-8 transition-colors duration-300 hover:border-white/20 ${className}`}
  >
    {children}
  </motion.div>
);

export const IconBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-button shadow-glow">
    {children}
  </div>
);
